import { useCallback, useEffect, useState } from 'react';
// Pure: every field of an edited row in the shape its column already holds. The phone's own.
import { applyDraft, displayValue } from '@/webapp/content';
import { columnExample } from '@/webapp/columnExample';
import {
  NOT_PUBLISHED, addManagedRow, columnsOf, fetchManagedRows, removeManagedRow, updateManagedRow,
  type ManagedRow,
} from '../../../api/managedRows';
import { Button, Icon, TextField } from '../../../ui';
import ConfirmDialog from './ConfirmDialog';

/** Long enough that one line would hide most of it. */
const LONG = 40;

/**
 * Rows the site shows that the owner changes after publishing — the phone's Live tab, in its
 * words (src/webapp/WebAppAdminPanel.tsx).
 *
 * The difference from Content is the reason it exists. Content rewrites the page and publishes
 * it again, which is right for a course list that changes monthly and wrong for this week's
 * tee times. These rows live on the server: saved here, they are live for every visitor on
 * their next look, with no rebuild, and nothing about the page's source changes.
 */
export default function LiveSection({
  hostedAppId,
  collections,
}: {
  /** The catalogue id the rows live under. None until the site has been published. */
  hostedAppId: string | undefined;
  /** Each `S.list` collection, and the fields the app READS off it (schemasFromSource). */
  collections: { name: string; fields: string[] }[];
}) {
  const [rows, setRows] = useState<Record<string, ManagedRow[]>>({});
  /** Lists that came back short of the whole collection. The rows still show. */
  const [truncated, setTruncated] = useState<string[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState<{ collection: string; id: string | null } | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [long, setLong] = useState<string[]>([]);
  const [newColumn, setNewColumn] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  /** What went wrong with the last save or delete, under the phone's title for it. */
  const [error, setError] = useState<{ title: string; message: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const names = collections.map((c) => c.name).join('\n');
  const load = useCallback(async () => {
    if (!hostedAppId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const next: Record<string, ManagedRow[]> = {};
      const short: string[] = [];
      for (const name of names.split('\n').filter(Boolean)) {
        const page = await fetchManagedRows(hostedAppId, name);
        next[name] = page.rows;
        if (!page.complete) short.push(name);
      }
      setTruncated(short);
      setRows(next);
    } catch (e) {
      setLoadError(e instanceof Error && e.message ? e.message : "Couldn't load these just now.");
    } finally {
      setLoading(false);
    }
  }, [hostedAppId, names]);

  useEffect(() => {
    void load();
  }, [load]);

  // What the app reads, for a list with no rows to learn the columns from.
  const expected = (name: string) => collections.find((c) => c.name === name)?.fields ?? [];

  function open(collection: string, row: ManagedRow | null): void {
    const known = columnsOf(rows[collection] ?? []);
    const columns = known.length ? known : expected(collection);
    const values: Record<string, string> = {};
    for (const c of columns) values[c] = row ? displayValue(row[c]) : '';
    setDraft(values);
    setLong(columns.filter((c) => values[c].length > LONG));
    setNewColumn(null);
    setError(null);
    setEditing({ collection, id: row?.id ?? null });
  }

  async function save(): Promise<void> {
    if (!editing || saving) return;
    setError(null);
    setSaving(true);
    try {
      const existing = (rows[editing.collection] ?? []).find((r) => r.id === editing.id);
      // The shape each column already holds, so what the app reads back is what it wrote. A new
      // row has nothing of its own yet, so its neighbours say what shape it should arrive in.
      const siblings = (rows[editing.collection] ?? []).filter((r) => r.id !== editing.id);
      const body = applyDraft(existing, draft, siblings);
      if (editing.id) await updateManagedRow(hostedAppId, editing.collection, editing.id, body);
      else await addManagedRow(hostedAppId, editing.collection, body);
      setEditing(null);
      await load();
    } catch (e) {
      setError({ title: "Couldn't save.", message: e instanceof Error && e.message ? e.message : 'Please try again.' });
    } finally {
      setSaving(false);
    }
  }

  async function remove(): Promise<void> {
    setDeleting(false);
    if (!editing?.id) return;
    setError(null);
    setSaving(true);
    try {
      await removeManagedRow(hostedAppId, editing.collection, editing.id);
      setEditing(null);
      await load();
    } catch (e) {
      setError({ title: "Couldn't delete.", message: e instanceof Error && e.message ? e.message : 'Please try again.' });
    } finally {
      setSaving(false);
    }
  }

  if (!hostedAppId) return <p className="type-body">{NOT_PUBLISHED}</p>;

  if (loading) return <p className="type-body">Loading…</p>;

  if (loadError) {
    return (
      <div className="manage-list">
        <p role="alert" className="type-body">{loadError}</p>
        <Button title="Try again" variant="ghost" accessibilityLabel="Try loading again" onPress={() => void load()} />
      </div>
    );
  }

  if (editing) {
    const fields = Object.keys(draft);
    // The rows already there are the truest example of what the app expects: it wrote them.
    const siblings = (rows[editing.collection] ?? []).filter((r) => r.id !== editing.id);
    return (
      <div className="manage-edit">
        <h3 className="type-heading">{editing.id ? 'Edit row' : 'New row'}</h3>
        {fields.map((f) => (
          <TextField
            key={f}
            label={f}
            value={draft[f] ?? ''}
            onChangeText={(v) => setDraft((d) => ({ ...d, [f]: v }))}
            placeholder={columnExample(f, siblings)}
            multiline={long.includes(f)}
          />
        ))}

        {newColumn === null ? (
          <Button
            title={fields.length ? 'Add a field' : 'Add the first field'}
            variant="ghost"
            accessibilityLabel="Add a field to this row"
            onPress={() => setNewColumn('')}
          />
        ) : (
          <div>
            <TextField label="Name for the new field" value={newColumn} onChangeText={setNewColumn} placeholder="day" autoFocus />
            <div className="build-actions">
              <Button
                title="Add field"
                variant="secondary"
                disabled={!newColumn.trim()}
                accessibilityLabel="Add this field"
                onPress={() => {
                  const name = newColumn.trim();
                  if (!name) return;
                  setDraft((d) => ({ ...d, [name]: d[name] ?? '' }));
                  setNewColumn(null);
                }}
              />
              <Button title="Cancel" variant="ghost" accessibilityLabel="Cancel adding a field" onPress={() => setNewColumn(null)} />
            </div>
          </div>
        )}

        {error && (
          <p role="alert" className="type-caption field-error-text">
            <strong>{error.title}</strong> {error.message}
          </p>
        )}

        <div className="build-actions">
          <Button
            title="Save — live straight away"
            loading={saving}
            disabled={fields.length === 0}
            accessibilityLabel="Save this row — it goes live straight away"
            onPress={() => void save()}
          />
          <Button title="Cancel" variant="ghost" accessibilityLabel="Cancel editing" onPress={() => setEditing(null)} />
        </div>
        {editing.id && (
          <Button
            title="Delete row"
            variant="dangerSoft"
            disabled={saving}
            accessibilityLabel="Delete this row"
            onPress={() => setDeleting(true)}
          />
        )}

        <ConfirmDialog
          question={
            deleting
              ? { title: 'Delete this row?', body: 'It disappears from the app straight away.', confirm: 'Delete', cancel: 'Keep it' }
              : null
          }
          onCancel={() => setDeleting(false)}
          onConfirm={() => void remove()}
        />
      </div>
    );
  }

  return (
    <div className="manage-list">
      <p className="type-caption">These change for everyone as soon as you save. No rebuild.</p>
      {collections.map(({ name }) => {
        const items = rows[name] ?? [];
        const columns = columnsOf(items);
        const reads = expected(name);
        return (
          <section key={name} aria-label={name}>
            <h3 className="type-heading">
              {name} <span className="type-caption project-meta">{items.length}</span>
            </h3>
            {/* Said, because the count above would otherwise read as the whole list. */}
            {truncated.includes(name) && (
              <p className="type-caption">
                {`This list is longer than this screen can load. You are editing the first ${items.length} rows; the rest are live on the page and not shown here.`}
              </p>
            )}
            {items.length === 0 && (
              <p className="type-caption">
                {reads.length
                  ? `Nothing here yet. The app reads: ${reads.join(', ')}.`
                  : 'Nothing here yet — the app shows its empty state until you add a row.'}
              </p>
            )}
            <ul className="project-list">
              {items.map((row) => {
                const title = displayValue(row[columns[0]]);
                const sub = columns[1] ? displayValue(row[columns[1]]) : '';
                return (
                  <li key={row.id}>
                    <button
                      type="button"
                      className="card clay-card project-row pressable"
                      aria-label={`Edit ${title || 'this row'}`}
                      onClick={() => open(name, row)}
                    >
                      <span className="project-text">
                        <span className="type-heading">{title || 'Row'}</span>
                        {sub && <span className="type-caption project-meta">{sub}</span>}
                      </span>
                      <Icon name="chevron" size={18} color="var(--color-chevron)" />
                    </button>
                  </li>
                );
              })}
            </ul>
            <Button title="Add row" variant="ghost" accessibilityLabel={`Add a row to ${name}`} onPress={() => open(name, null)} />
          </section>
        );
      })}
    </div>
  );
}
